import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import LoginRequiredConfirm from '@/components/feature/LoginRequiredConfirm';
import { dispatchToast } from '@/components/ui/GlobalToast';
import { getPublicSolutions, type AttachmentItem, type LinkItem, type SolutionDto } from '@/api/solution';
import { recordShare } from '@/api/interaction';
import { useAuth } from '@/hooks/useAuth';
import { useCollect } from '@/hooks/useCollect';
import { usePageView } from '@/hooks/usePageView';
import { useHistory } from '@/hooks/useHistory';
import { useAttachmentDownload } from '@/hooks/useAttachmentDownload';
import { useLike } from '@/hooks/useLike';
import AttachmentPanel from '@/pages/solutions/detail/components/AttachmentPanel';
import SolutionRichDetailModal from '@/pages/solutions/detail/components/SolutionRichDetailModal';
import { hasRichHtmlContent } from '@/components/ui/RichTextEditor';
import { getPublicPointsOverview } from '@/api/points';
import { fetchDictBatch } from '@/api/dict';
import { usePublicSubPageContent } from '@/hooks/usePublicSubPageContent';

function readText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function isValidLinkUrl(value: unknown): value is string {
  const text = readText(value);
  if (!text) return false;
  return /^https?:\/\//i.test(text) || text.startsWith('/');
}

function normalizeScore(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  if (value < 0 || value > 100) return null;
  return Math.round(value);
}

function clampStars(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(5, Math.round(value)));
}

type SolutionDetailModalState = {
  type: 'commercial' | 'opensource';
  title: string;
  subtitle: string;
  html: string;
  accentClass: string;
} | null;

export default function SolutionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [allSolutions, setAllSolutions] = useState<SolutionDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { content: subPageContent, error: subPageError } = usePublicSubPageContent();
  const [dictCategories, setDictCategories] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    fetchDictBatch(['solution_category'])
      .then((dicts) => {
        if (dicts['solution_category']) {
          setDictCategories(dicts['solution_category'].map((x) => ({ label: x.label, value: x.value })));
        }
      })
      .catch((err) => console.error('Failed to load solution categories in detail page:', err));
  }, []);

  const getCategoryLabel = useCallback((cat: string) => {
    if (!cat) return '无';
    if (cat === '全部') return '全部';
    const found = dictCategories.find((c) => c.value === cat);
    return found ? found.label : cat;
  }, [dictCategories]);

  const refreshSolutions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getPublicSolutions();
      setAllSolutions(Array.isArray(res?.list) ? res.list : []);
    } catch (err) {
      setAllSolutions([]);
      setLoadError(err instanceof Error ? err.message : '获取方案详情失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSolutions();
    const handleRefresh = () => void refreshSolutions();
    window.addEventListener('wanxiang_solutions_updated', handleRefresh);
    return () => window.removeEventListener('wanxiang_solutions_updated', handleRefresh);
  }, [refreshSolutions]);

  const solution = useMemo(
    () => allSolutions.find((s) => String(s.id) === String(id ?? '')),
    [allSolutions, id],
  );

  const solutionId = solution?.id ? String(solution.id) : '';
  const solutionTitle = readText(solution?.title) || '';
  const solutionImage = readText(solution?.image);
  const commercialName = readText(solution?.commercial);
  const openSourceName = readText(solution?.opensource);
  const description = readText(solution?.description);
  const category = readText(solution?.category);
  const costSaving = readText(solution?.costSaving);
  const commercialCost = readText(solution?.costCompare?.commercial);
  const openSourceCost = readText(solution?.costCompare?.opensource);
  const commercialDetail = typeof solution?.commercialDetail === 'string' ? solution.commercialDetail : '';
  const opensourceDetail = typeof solution?.opensourceDetail === 'string' ? solution.opensourceDetail : '';
  const hasCommercialDetail = hasRichHtmlContent(commercialDetail);
  const hasOpensourceDetail = hasRichHtmlContent(opensourceDetail);
  const stars = clampStars(solution?.stars);
  const commercialScore = normalizeScore(solution?.functionalScore?.commercial);
  const openSourceScore = normalizeScore(solution?.functionalScore?.opensource);

  const commercialFeatures = useMemo(
    () => (solution?.features ?? []).map((item) => readText(item)).filter((item): item is string => Boolean(item)),
    [solution?.features],
  );
  const openSourceFeatures = useMemo(
    () => (solution?.openSourceFeatures ?? []).map((item) => readText(item)).filter((item): item is string => Boolean(item)),
    [solution?.openSourceFeatures],
  );
  const tags = useMemo(
    () => (solution?.tags ?? []).map((item) => readText(item)).filter((item): item is string => Boolean(item)),
    [solution?.tags],
  );
  const attachments: AttachmentItem[] = useMemo(
    () => (solution?.attachments ?? []).filter((item): item is AttachmentItem => Boolean(readText(item?.name))),
    [solution?.attachments],
  );
  const links = useMemo(
    () =>
      (solution?.links ?? [])
        .map((link, index) => {
          const title = readText((link as any).title) || readText(link.label);
          const url = readText(link.url);
          if (!title || !url || !isValidLinkUrl(url)) return null;
          return {
            key: `${link.type}-${index}-${url}`,
            title,
            url,
            type: link.type,
          };
        })
        .filter((item): item is { key: string; title: string; url: string; type: LinkItem['type'] } => Boolean(item)),
    [solution?.links],
  );

  const { isLoggedIn } = useAuth();
  const { isCollected, toggle } = useCollect();
  const { record } = useHistory();
  const favored = solutionId ? isCollected(solutionId, 'solution') : false;
  const viewCount = usePageView('solution', solutionId);
  const [showLoginConfirm, setShowLoginConfirm] = useState(false);
  const {
    likeCount,
    isLiked,
    toggle: toggleLike,
    featureEnabled: likeEnabled,
  } = useLike('solution', solutionId || 'solution-detail', solutionTitle || '方案');

  // 分享功能开关状态
  const [shareEnabled, setShareEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const overview = await getPublicPointsOverview(1);
        if (!active) return;
        if (typeof overview.features?.share !== 'boolean') {
          throw new Error('分享功能开关响应格式错误');
        }
        setShareEnabled(overview.features.share);
      } catch (err) {
        console.error('Failed to sync share feature switch in solution detail:', err);
        if (!active) return;
        setShareEnabled(false);
      }
    };
    const handleRefresh = () => { void refresh(); };
    void refresh();
    window.addEventListener('wanxiang_feature_updated', handleRefresh);
    window.addEventListener('wanxiang_settings_updated', handleRefresh);
    return () => {
      active = false;
      window.removeEventListener('wanxiang_feature_updated', handleRefresh);
      window.removeEventListener('wanxiang_settings_updated', handleRefresh);
    };
  }, []);

  const handleLike = async () => {
    if (!solutionId) return;
    if (!isLoggedIn) {
      setShowLoginConfirm(true);
      return;
    }
    const result = await toggleLike();
    if (result.success) {
      dispatchToast(result.message.includes('取消') ? 'info' : 'success', result.message);
      return;
    }
    dispatchToast('error', result.message || '点赞失败，请稍后重试');
  };

  const [pdfPreview, setPdfPreview] = useState<{ previewUrl: string; name: string } | null>(null);
  const [detailModal, setDetailModal] = useState<SolutionDetailModalState>(null);
  const [showCommunity, setShowCommunity] = useState(false);
  const {
    toast: attachToast,
    showLoginHint,
    dismissLoginHint,
    checkAndDownload,
    getAttachmentStatus,
  } = useAttachmentDownload('solution', solutionId);

  const closePdfPreview = useCallback(() => {
    setPdfPreview(null);
  }, []);

  const closeDetailModal = useCallback(() => {
    setDetailModal(null);
  }, []);

  const handleShare = async () => {
    if (!solutionId) return;
    const url = window.location.href;
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      copied = document.execCommand('copy');
      document.body.removeChild(el);
    }
    if (!copied) {
      dispatchToast('error', '链接复制失败，请稍后重试');
      return;
    }

    if (isLoggedIn) {
      try {
        const res = await recordShare({
          targetType: 'solution',
          targetId: solutionId,
          targetTitle: solutionTitle || '方案',
          channel: 'copy_link',
        });
        if (res.rewarded) {
          window.dispatchEvent(new CustomEvent('wanxiang_points_updated'));
        }
        dispatchToast(res.rewarded ? 'success' : 'info', res.message);
      } catch (err) {
        dispatchToast('error', err instanceof Error ? err.message : '分享奖励发放失败');
      }
    } else {
      dispatchToast('success', '链接已复制！');
    }
  };

  useEffect(() => {
    if (!solutionId || !solutionTitle) return;
    void record({
      id: solutionId,
      type: 'solution',
      title: solutionTitle,
      image: solutionImage || '',
      category: category || '',
    });
  }, [record, solutionId, solutionTitle, solutionImage, category]);

  const handleFavor = async () => {
    if (!solutionId) return;
    if (!isLoggedIn) {
      setShowLoginConfirm(true);
      return;
    }
    const result = await toggle(solutionId, 'solution');
    if (!result.success) {
      dispatchToast('error', result.message || '收藏操作失败，请稍后重试');
      return;
    }
    dispatchToast(result.isCollected ? 'success' : 'info', result.message);
  };

  const difficultyLabel = ['', '简单', '较易', '中等', '较难', '困难'];
  const difficultyColor = ['', '#10b981', '#10b981', '#f97316', '#ef4444', '#dc2626'];
  const difficultyValue =
    typeof solution?.deployDifficulty === 'number' &&
      solution.deployDifficulty >= 1 &&
      solution.deployDifficulty <= 5
      ? solution.deployDifficulty
      : 0;
  const difficultyText = difficultyValue ? difficultyLabel[difficultyValue] : null;
  const difficultyTextColor = difficultyValue ? difficultyColor[difficultyValue] : '#111827';

  const related = useMemo(() => {
    if (!solution || !category) return [];
    return allSolutions.filter((s) => String(s.id) !== solutionId && readText(s.category) === category);
  }, [allSolutions, solution, category, solutionId]);

  const relatedList = useMemo(() => {
    if (!solution) return [];
    if (related.length > 0) return related.slice(0, 3);
    return allSolutions.filter((s) => String(s.id) !== solutionId).slice(0, 3);
  }, [allSolutions, related, solution, solutionId]);

  const hasFeatureSection = commercialFeatures.length > 0 || openSourceFeatures.length > 0;
  const hasScoreSection = commercialScore !== null || openSourceScore !== null;

  const linkIconMap: Record<string, { icon: string; color: string }> = {
    github: { icon: 'ri-github-line', color: 'text-gray-700' },
    docs: { icon: 'ri-book-open-line', color: 'text-[#0ea5e9]' },
    demo: { icon: 'ri-play-circle-line', color: 'text-[#10b981]' },
    video: { icon: 'ri-video-line', color: 'text-red-500' },
    other: { icon: 'ri-link', color: 'text-gray-400' },
    download: { icon: 'ri-download-line', color: 'text-[#0ea5e9]' },
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500">
            正在加载方案详情...
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
          <div className="bg-white rounded-2xl border border-red-100 p-10 text-center">
            <p className="text-red-500 mb-4">方案数据加载失败：{loadError}</p>
            <button
              onClick={() => void refreshSolutions()}
              className="px-4 py-2 bg-[#0ea5e9] text-white text-sm rounded-lg hover:bg-[#0284c7] transition-colors cursor-pointer"
            >
              重试
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!solution) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <p className="text-gray-700 mb-2">未找到该方案</p>
            <p className="text-sm text-gray-400 mb-6">该方案可能已下架或不存在。</p>
            <Link
              to="/solutions"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0ea5e9] text-white text-sm rounded-lg hover:bg-[#0284c7] transition-colors"
            >
              返回方案列表
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {detailModal && (
        <SolutionRichDetailModal
          open
          title={detailModal.title}
          subtitle={detailModal.subtitle}
          html={detailModal.html}
          accentClass={detailModal.accentClass}
          onClose={closeDetailModal}
        />
      )}
      {pdfPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4" onClick={closePdfPreview}>
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
                  <i className="ri-file-pdf-2-line text-red-500"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{pdfPreview.name}</h3>
                  <p className="text-xs text-gray-400">PDF 在线预览</p>
                </div>
              </div>
              <button onClick={closePdfPreview} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="flex-1 bg-gray-100 relative">
              <object
                data={pdfPreview.previewUrl}
                type="application/pdf"
                className="w-full h-full border-0"
                aria-label={pdfPreview.name}
              >
                <iframe
                  src={`${pdfPreview.previewUrl}#toolbar=0&navpanes=0`}
                  className="w-full h-full border-0"
                  title={pdfPreview.name}
                />
              </object>
            </div>
          </div>
        </div>
      )}
      {showCommunity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowCommunity(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#f97316]/10 rounded-2xl mx-auto mb-4">
              <i className="ri-rocket-line text-[#f97316] text-3xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">敬请期待</h3>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">社区功能正在紧锣密鼓地开发中，即将上线！</p>
            <button onClick={() => setShowCommunity(false)} className="w-full py-2.5 bg-[#f97316] text-white font-semibold rounded-xl hover:bg-[#ea6c0a] transition-all cursor-pointer whitespace-nowrap">我知道了</button>
          </div>
        </div>
      )}

      {showLoginHint && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[300] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-7 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 flex items-center justify-center bg-[#0ea5e9]/10 rounded-2xl mx-auto mb-4">
              <i className="ri-lock-password-line text-[#0ea5e9] text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 text-base mb-2">需要登录才能下载</h3>
            <p className="text-gray-500 text-sm mb-5 leading-relaxed">该附件需要登录后才能下载，登录后还可以收藏方案、记录浏览足迹。</p>
            <div className="flex gap-3">
              <button onClick={dismissLoginHint} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">稍后再说</button>
              <button
                onClick={() => {
                  dismissLoginHint();
                  navigate('/login', { state: { from: location.pathname } });
                }}
                className="flex-1 py-2.5 bg-[#0ea5e9] text-white text-sm font-medium rounded-xl hover:bg-[#0284c7] cursor-pointer whitespace-nowrap"
              >立即登录</button>
            </div>
          </div>
        </div>
      )}

      <LoginRequiredConfirm
        open={showLoginConfirm}
        title="需要登录后访问"
        description="该功能需要登录后才能继续，是否前往登录页面？"
        onConfirm={() => {
          setShowLoginConfirm(false);
          navigate('/login', { state: { from: location.pathname } });
        }}
        onCancel={() => setShowLoginConfirm(false)}
      />

      {attachToast && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[200] px-5 py-2.5 text-white text-sm rounded-full flex items-center gap-2 ${attachToast.type === 'success' ? 'bg-[#10b981]' : attachToast.type === 'error' ? 'bg-red-500' : 'bg-gray-800/90'
          }`}>
          <i className={`${attachToast.type === 'success' ? 'ri-checkbox-circle-line' : attachToast.type === 'error' ? 'ri-error-warning-line' : 'ri-information-line'} text-base`}></i>
          {attachToast.msg}
        </div>
      )}

      <Navbar />

      <div className="relative h-72 overflow-hidden">
        {solutionImage ? (
          <img src={solutionImage} alt={solutionTitle || '方案封面'} className="w-full h-full object-cover object-top" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center">
            <span className="text-white/90 text-sm">暂无封面</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Link to="/solutions" className="text-white/60 text-sm hover:text-white cursor-pointer">方案库</Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <span className="text-white/80 text-sm">{solutionTitle || '无'}</span>
          </div>
          <div className="flex items-end justify-between gap-4">
            <h1 className="text-3xl md:text-4xl font-bold text-white">{solutionTitle || '无'}</h1>
            <div className="flex items-center gap-2 shrink-0">
              {likeEnabled && (
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${isLiked ? 'bg-[#f97316] text-white' : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
                    }`}
                  title={isLiked ? '取消点赞' : '点赞此方案'}
                >
                  <i className={isLiked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                  {likeCount > 0 ? likeCount : ''} {isLiked ? '已点赞' : '点赞'}
                </button>
              )}
              {shareEnabled && (
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
                  title="复制链接分享"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              )}
              <button
                onClick={handleFavor}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${favored ? 'bg-red-500 text-white' : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
                  }`}
              >
                <i className={favored ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                {favored ? '已收藏' : '收藏方案'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <h2 className="font-bold text-gray-900 text-lg mb-4">方案概述</h2>
              <p className="text-gray-600 leading-relaxed mb-6">{description || '无'}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-xl p-4 border border-red-100 flex flex-col min-h-[108px]">
                  <div className="text-xs text-red-400 mb-1">商业方案</div>
                  <div className="font-bold text-gray-900 text-base">{commercialName || '无'}</div>
                  <div className="mt-auto pt-3 flex items-end justify-between gap-2">
                    <div className="text-red-500 font-semibold">{commercialCost || '无'}</div>
                    {hasCommercialDetail && (
                      <button
                        type="button"
                        onClick={() => setDetailModal({
                          type: 'commercial',
                          title: commercialName || '商业方案',
                          subtitle: '商业方案详情',
                          html: commercialDetail,
                          accentClass: 'text-red-500',
                        })}
                        className="text-xs text-[#0ea5e9] hover:text-[#0284c7] hover:underline cursor-pointer whitespace-nowrap shrink-0"
                      >
                        查看详情
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-[#0ea5e9]/5 rounded-xl p-4 border border-[#0ea5e9]/20 flex flex-col min-h-[108px]">
                  <div className="text-xs text-[#0ea5e9] mb-1">平替方案</div>
                  <div className="font-bold text-[#0ea5e9] text-base">{openSourceName || '无'}</div>
                  <div className="mt-auto pt-3 flex items-end justify-between gap-2">
                    <div className="text-[#10b981] font-semibold">{openSourceCost || '无'}</div>
                    {hasOpensourceDetail && (
                      <button
                        type="button"
                        onClick={() => setDetailModal({
                          type: 'opensource',
                          title: openSourceName || '平替方案',
                          subtitle: '平替方案详情',
                          html: opensourceDetail,
                          accentClass: 'text-[#0ea5e9]',
                        })}
                        className="text-xs text-[#0ea5e9] hover:text-[#0284c7] hover:underline cursor-pointer whitespace-nowrap shrink-0"
                      >
                        查看详情
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {hasFeatureSection && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h2 className="font-bold text-gray-900 text-lg mb-4">功能对比</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-gray-400"></span>商业方案功能
                    </h3>
                    <ul className="space-y-2">
                      {commercialFeatures.length > 0 ? (
                        commercialFeatures.map((f) => (
                          <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-check-line text-gray-400"></i>{f}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-gray-400">无</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#0ea5e9] mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#0ea5e9]"></span>平替方案优势
                    </h3>
                    <ul className="space-y-2">
                      {openSourceFeatures.length > 0 ? (
                        openSourceFeatures.map((f) => (
                          <li key={f} className="flex items-center gap-2 text-sm text-[#0ea5e9]">
                            <i className="ri-check-double-line text-[#0ea5e9]"></i>{f}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-gray-400">无</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {hasScoreSection && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h2 className="font-bold text-gray-900 text-lg mb-6">综合评分对比</h2>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">商业方案 ({commercialName || '无'})</span>
                      <span className="font-semibold text-gray-900">{commercialScore !== null ? `${commercialScore}分` : '无'}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gray-400 rounded-full transition-all duration-1000" style={{ width: `${commercialScore ?? 0}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#0ea5e9]">平替方案 ({openSourceName || '无'})</span>
                      <span className="font-semibold text-[#0ea5e9]">{openSourceScore !== null ? `${openSourceScore}分` : '无'}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#0ea5e9] rounded-full transition-all duration-1000" style={{ width: `${openSourceScore ?? 0}%` }}></div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-4">* 评分基于功能完整性，平替方案在成本和数据自主性上具有显著优势</p>
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div className="bg-white rounded-2xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-4">方案信息</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">分类</span>
                  <span className="font-medium text-gray-900">{getCategoryLabel(category || '')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">部署难度</span>
                  <span className="font-medium" style={{ color: difficultyText ? difficultyTextColor : '#111827' }}>
                    {difficultyText || '无'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">成本节省</span>
                  <span className="font-medium text-[#10b981]">{costSaving || '无'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">浏览次数</span>
                  <span className="font-medium text-gray-900 flex items-center gap-1">
                    <i className="ri-eye-line text-xs text-gray-400"></i>{viewCount === null ? '—' : viewCount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">推荐指数</span>
                  {stars > 0 ? (
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className={`ri-star-fill text-sm ${i < stars ? 'text-[#f97316]' : 'text-gray-200'}`}></i>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">无</span>
                  )}
                </div>
              </div>
            </div>

            {tags.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3">标签</h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-[#0ea5e9]/10 text-[#0ea5e9] text-xs rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            <AttachmentPanel
              attachments={attachments}
              checkAndDownload={checkAndDownload}
              getAttachmentStatus={getAttachmentStatus}
              onRequireLogin={() => { }}
              onPdfPreview={(previewUrl, name) => setPdfPreview({ previewUrl, name })}
            />

            {links.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">相关链接</h3>
                  <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{links.length} 个链接</span>
                </div>
                <ul className="space-y-1">
                  {links.map((link) => {
                    const { icon: lIcon, color: lColor } = linkIconMap[link.type] || linkIconMap.other;
                    return (
                      <li key={link.key}>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-gray-50 transition-colors group cursor-pointer"
                        >
                          <div className="w-7 h-7 flex items-center justify-center shrink-0">
                            <i className={`${lIcon} ${lColor} text-lg`}></i>
                          </div>
                          <span className="flex-1 text-sm text-gray-700 group-hover:text-[#0ea5e9] transition-colors truncate">
                            {link.title}
                          </span>
                          <div className="w-5 h-5 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-external-link-line text-gray-400 text-sm"></i>
                          </div>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-2xl p-5 text-white">
              <h3 className="font-bold mb-2">{subPageContent.solutionDetail.helpCard.title || '无'}</h3>
              <p className="text-white/80 text-sm mb-4">{subPageContent.solutionDetail.helpCard.desc || '无'}</p>
              {subPageError && <p className="text-xs text-white/70 mb-3">区块内容加载失败：{subPageError}</p>}
              <button
                onClick={() => setShowCommunity(true)}
                className="block w-full text-center py-2 bg-white text-[#0ea5e9] font-semibold text-sm rounded-xl hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap"
              >
                {subPageContent.solutionDetail.helpCard.btnText || '无'}
              </button>
            </div>

            <Link to="/solutions" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
              <i className="ri-arrow-left-line"></i>返回方案列表
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="border-t border-gray-100 pt-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {related.length > 0
                  ? `${subPageContent.solutionDetail.relatedTitle || '无'}「${category || '当前'}」方案`
                  : (subPageContent.solutionDetail.relatedAltTitle || '无')}
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                {related.length > 0
                  ? (subPageContent.solutionDetail.relatedSubtitle || '无')
                  : (subPageContent.solutionDetail.relatedAltSubtitle || '无')}
              </p>
            </div>
            <Link to="/solutions" className="flex items-center gap-1.5 text-sm text-[#0ea5e9] hover:text-[#0284c7] font-medium cursor-pointer whitespace-nowrap transition-colors">
              查看全部方案<i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {relatedList.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-sm text-gray-400">
              暂无可展示的相关方案
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedList.map((s) => {
                const itemId = String(s.id);
                const itemTitle = readText(s.title) || '无';
                const itemCategory = readText(s.category) || '无';
                const itemDescription = readText(s.description) || '无';
                const itemCommercial = readText(s.commercial) || '无';
                const itemOpenSource = readText(s.opensource) || '无';
                const itemCostSaving = readText(s.costSaving) || '无';
                const itemImage = readText(s.image);
                return (
                  <Link
                    key={itemId}
                    to={`/solutions/${itemId}`}
                    className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#0ea5e9]/40 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      {itemImage ? (
                        <img src={itemImage} alt={itemTitle} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center text-white/90 text-xs">
                          暂无封面
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 bg-[#0ea5e9] text-white text-xs font-medium rounded-md">{getCategoryLabel(itemCategory)}</span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-[#f97316] text-white text-xs font-medium rounded-md">省 {itemCostSaving}</span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 text-base mb-1 group-hover:text-[#0ea5e9] transition-colors">{itemTitle}</h3>
                      <p className="text-gray-400 text-xs mb-3 line-clamp-2 leading-relaxed">{itemDescription}</p>
                      <div className="flex items-center gap-2 text-xs mb-3">
                        <span className="text-gray-400 line-through">{itemCommercial}</span>
                        <i className="ri-arrow-right-line text-gray-300"></i>
                        <span className="text-[#0ea5e9] font-semibold">{itemOpenSource}</span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-[#10b981] font-semibold">
                          <i className="ri-money-cny-circle-line"></i>节省 {itemCostSaving}
                        </div>
                        <span className="flex items-center gap-1 text-xs text-[#0ea5e9] font-medium group-hover:gap-2 transition-all">
                          查看详情<i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
